'use strict';

var name = "@nrk/core-toggle";
var version = "2.2.2";

var IS_BROWSER = typeof window !== 'undefined';
var IS_ANDROID = IS_BROWSER && /(android)/i.test(navigator.userAgent); // Bad, but needed
var IS_IOS = IS_BROWSER && /iPad|iPhone|iPod/.test(String(navigator.platform));
var HAS_EVENT_OPTIONS = (function (has) {
  if ( has === void 0 ) has = false;

  try { window.addEventListener('test', null, { get passive () { has = true; } }); } catch (e) {}
  return has
})();

/**
* addEvent
* @param {String} uuid An unique ID of the event to bind - ensurnes single instance
* @param {String} type The type of event to bind
* @param {Function} handler The function to call on event
* @param {Boolean|Object} options useCapture or options object for addEventListener. Defaults to false
*/
function addEvent (uuid, type, handler, options) {
  if ( options === void 0 ) options = false;

  if (typeof window === 'undefined' || window[uuid = uuid + "-" + type]) { return } // Ensure single instance
  if (!HAS_EVENT_OPTIONS && typeof options === 'object') { options = Boolean(options.capture); } // Fix unsupported options
  var node = (type === 'resize' || type === 'load') ? window : document;
  node.addEventListener(window[uuid] = type, handler, options);
}

/**
* dispatchEvent - with infinite loop prevention
* @param {Element} elem The target object
* @param {String} name The source object(s)
* @param {Object} detail Detail object (bubbles and cancelable is set to true)
* @return {Boolean} Whether the event was canceled
*/
var IGNORE = 'prevent_recursive_dispatch_maximum_callstack';
function dispatchEvent (element, name, detail) {
  if ( detail === void 0 ) detail = {};

  var ignore = "" + IGNORE + name;
  var event;

  if (element[ignore]) { return true } // We are already processing this event, so skip sending a new one
  else { element[ignore] = true; } // Add name to dispatching ignore

  if (typeof window.CustomEvent === 'function') {
    event = new window.CustomEvent(name, { bubbles: true, cancelable: true, detail: detail });
  } else {
    event = document.createEvent('CustomEvent');
    event.initCustomEvent(name, true, true, detail);
  }
  // IE reports incorrect event.defaultPrevented
  // but correct return value on element.dispatchEvent
  var result = element.dispatchEvent(event);
  element[ignore] = null; // Remove name from dispatching ignore

  return result // Follow W3C standard for return value
}

/**
* getUUID
* @return {String} A generated unique ID
*/
function getUUID (el) {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 5)
}

/**
* queryAll
* @param {String|NodeList|Array|Element} elements A CSS selector string, nodeList, element array, or single element
* @return {Element[]} Array of elements
*/
function queryAll (elements, context) {
  if ( context === void 0 ) context = document;

  if (elements) {
    if (elements.nodeType) { return [elements] }
    if (typeof elements === 'string') { return [].slice.call(context.querySelectorAll(elements)) }
    if (elements.length) { return [].slice.call(elements) }
  }
  return []
}

var UUID = ("data-" + name + "-" + version).replace(/\W+/g, '-'); // Strip invalid attribute characters
var ARIA = IS_ANDROID ? 'data' : 'aria'; // Andriod has a bug and reads only label instead of content
var KEYS = { ESC: 27 };

function toggle (toggles, open) {
  var options = typeof open === 'object' ? open : { open: open };
  if (IS_IOS) { document.documentElement.style.cursor = 'pointer'; } // Fix iOS events for closing popups (https://stackoverflow.com/a/16006333/8819615)

  return queryAll(toggles).map(function (toggle) {
    var content = getContentElement(toggle);
    var isOpen = toggle.getAttribute('aria-expanded') === 'true';
    var open = typeof options.open === 'boolean' ? options.open : (options.open === 'toggle' ? !isOpen : isOpen);
    var popup = String((options.hasOwnProperty('popup') ? options.popup : toggle.getAttribute(UUID)) || false);

    if (options.value) { toggle.innerHTML = options.value; } // Set innerHTML before updating aria-label
    if (popup !== 'false' && popup !== 'true') { toggle.setAttribute('aria-label', ((toggle.textContent) + ", " + popup)); } // Only update aria-label if popup-mode

    toggle.setAttribute(UUID, popup); // aria-haspopup triggers forms mode in JAWS, therefore store in uuid
    toggle.setAttribute('aria-controls', content.id = content.id || getUUID());
    content.setAttribute((ARIA + "-labelledby"), toggle.id = toggle.id || getUUID());
    setOpen(toggle, open);
    return toggle
  })
}

function getContentElement (toggle) {
  return document.getElementById(toggle.getAttribute('aria-controls')) || toggle.nextElementSibling
}

addEvent(UUID, 'keydown', function (event) {
  if (event.keyCode !== KEYS.ESC) { return }
  for (var el = event.target; el; el = el.parentElement) {
    var toggle = (el.id && document.querySelector(("[aria-controls=\"" + (el.id) + "\"]"))) || el;

    if (toggle.getAttribute(UUID) !== 'false' && toggle.getAttribute('aria-expanded') === 'true') {
      event.preventDefault(); // Prevent leaving maximized window in Safari
      toggle.focus();
      return setOpen(toggle, false)
    }
  }
}, true); // Use capture to enable checking defaultPrevented (from ESC key) in parents

addEvent(UUID, 'click', function (ref) {
  var target = ref.target;
  var defaultPrevented = ref.defaultPrevented;

  if (defaultPrevented) { return false } // Do not toggle if someone run event.preventDefault()

  for (var el = target, item = (void 0); el; el = el.parentElement) {
    var toggle = item && el.id && document.querySelector(("[" + UUID + "][aria-controls=\"" + (el.id) + "\"]"));
    if ((el.nodeName === 'BUTTON' || el.nodeName === 'A') && !el.hasAttribute(UUID)) { item = el; } // interactive element clicked
    if (toggle) {
      dispatchEvent(toggle, 'toggle.select', {
        relatedTarget: getContentElement(toggle),
        currentTarget: item,
        value: item.textContent.trim()
      });
      break
    }
  }

  queryAll(("[" + UUID + "]")).forEach(function (toggle) {
    var open = toggle.getAttribute('aria-expanded') === 'true';
    var popup = toggle.getAttribute(UUID) !== 'false';
    var content = getContentElement(toggle);

    if (toggle.contains(target)) { setOpen(toggle, !open); } // Click on toggle
    else if (popup && open) { setOpen(toggle, content.contains(target)); } // Click in content or outside
  });
});

function setOpen (toggle, open) {
  var content = getContentElement(toggle);
  var isOpen = toggle.getAttribute('aria-expanded') === 'true';
  var willOpen = typeof open === 'boolean' ? open : (open === 'toggle' ? !isOpen : isOpen);
  var isUpdate = isOpen === willOpen || dispatchEvent(toggle, 'toggle', { relatedTarget: content, isOpen: isOpen, willOpen: willOpen });
  var nextOpen = isUpdate ? willOpen : toggle.getAttribute('aria-expanded') === 'true'; // dispatchEvent can change attributes
  var focus = !isOpen && nextOpen && content.querySelector('[autofocus]');

  if (focus) { setTimeout(function () { return focus && focus.focus(); }); } // Move focus on next render (if element stil exists)

  toggle.setAttribute('aria-expanded', nextOpen);
  content[nextOpen ? 'removeAttribute' : 'setAttribute']('hidden', '');
}

module.exports = toggle;
